import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { Helmet } from 'react-helmet-async'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { useFeatureFlagEnabled } from 'posthog-js/react'

import Department from './Department'
import InviteResultsPanel from './InviteResultsPanel'
import {
  BulkInviteCategories,
  bulkInviteResultRows,
  categorizeBulkResult,
  collectEmailsForSubmit,
  makeInviteValidator,
} from './inviteSectionUtils'
import MemberTable from './MemberTable'
import OtherDomainUsersTable, { DOMAIN_INFO_USER_LIMIT } from './OtherDomainUsersTable'
import ResendAllInvites from './ResendAllInvites'
import { RoleDropdown } from './RoleDropdown'
import SeatBreakdown from './SeatBreakdown'
import { SeatTypeDropdown } from './SeatTypeDropdown'
import { TeamDropdown } from './TeamDropdown'

import styles from './TeamPage.module.scss'

import { BulkInviteResponse } from '@/api/generated'
import { getTeamInviteLink } from '@/api/members'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import EmailChipInput from '@/components/EmailChipInput'
import Input from '@/components/Input'
import RemoveMemberConfirmDialog from '@/components/RemoveMemberConfirmDialog'
import { Spinner } from '@/components/Spinner'
import Tabs from '@/components/Tabs'
import { TeamMemberActionDialogs } from '@/components/TeamMemberActionDialogs'
import { useToast } from '@/components/Toast/ToastContext'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useEnterpriseTeams } from '@/hooks/useEnterpriseTeams'
import { useHasDepartments } from '@/hooks/useHasDepartments'
import { useRemovalUsageLine } from '@/hooks/useRemovalUsageLine'
import { useSeatSelectionEnabled } from '@/hooks/useSeatManagementEnabled'
import { useSortedMembers } from '@/hooks/useSortedMembers'
import { InviteInput, useTeamMemberActions } from '@/hooks/useTeamMemberActions'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { AppDispatch, RootState } from '@/store'
import { selectDomainUsers, selectTeamDomainStatus } from '@/store/features/domain/domainSlice'
import {
  fetchAllUsersThunk,
  removeMemberThunk,
  revokeInviteThunk,
  selectInvites,
  selectIsMembersLoading,
  selectMembers,
} from '@/store/features/member/memberSlice'
import {
  selectCurrentEnterprise,
  selectEmail,
  selectIsAdmin,
  selectIsScimEnabled,
} from '@/store/features/user/userSlice'
import { Invite, Member, ProductSeatType, Role, SettingType, TeamDomainStatus } from '@/types'
import { isValidChipEmail, normalizeEmailInput } from '@/utils/email'
import { EventName } from '@/utils/eventDefinitions'
import { AUTOMATIC_SEAT_TYPE_LABEL, seatTypeLabel } from '@/utils/seatType'

const INVITE_LINK_SEAT: Record<ProductSeatType, string> = {
  [ProductSeatType.Bundle]: `a ${seatTypeLabel[ProductSeatType.Bundle]}`,
  [ProductSeatType.Dictation]: `a ${seatTypeLabel[ProductSeatType.Dictation]} seat`,
  [ProductSeatType.NoAccess]: seatTypeLabel[ProductSeatType.NoAccess],
}

const TEAM_MEMBERS_TAB = 'Members'
const OTHER_DOMAIN_USERS_TAB = 'Other users on your domain'
const DEPARTMENT_TAB = 'Department'

const InviteSectionHeading = ({ hasDepts }: { hasDepts: boolean }) => (
  <div className={styles.heading}>{hasDepts ? 'Add to your organization' : 'Add your team'}</div>
)

export const InviteSection = ({
  onSubmit,
  isLoading,
  isScimEnabled,
  hasDepts,
  teamInsightsEnabled,
  teamInviteDepartmentPickerEnabled,
}: {
  onSubmit: (invites: InviteInput[], teamId?: string | null) => Promise<BulkInviteResponse | 'error' | undefined>
  isLoading: boolean
  isScimEnabled: boolean
  hasDepts: boolean
  teamInsightsEnabled: boolean
  teamInviteDepartmentPickerEnabled: boolean
}) => {
  const isAdmin = useSelector(selectIsAdmin)
  const currentEmail = useSelector(selectEmail)
  const [emails, setEmails] = useState<string[]>([])
  const [draft, setDraft] = useState('')
  const [role, setRole] = useState<Role>('member')
  // Never auto-selected, even for a single-team enterprise — an explicit pick
  // is required or the invite behaves exactly as it does today (no team_id).
  const [teamId, setTeamId] = useState<string | null>(null)
  // Only an explicit pick is sent; otherwise the server applies the org default when the invite is accepted.
  const [seatType, setSeatType] = useState<ProductSeatType | null>(null)
  const [bulkCategories, setBulkCategories] = useState<BulkInviteCategories | null>(null)
  const members = useSelector(selectMembers)
  const invitations = useSelector(selectInvites)
  const { updateToast } = useToast()
  const { trackEvent } = useAnalytics()
  const enterprise = useSelector(selectCurrentEnterprise)
  const enterpriseId = enterprise?.id ?? ''

  const showTeamDropdown = hasDepts && teamInsightsEnabled && teamInviteDepartmentPickerEnabled
  const seatSelectionEnabled = useSeatSelectionEnabled()
  const showSeatType = isAdmin && seatSelectionEnabled
  const intendedSeatType = showSeatType && role !== 'it_admin' ? (seatType ?? undefined) : undefined
  const orgDefaultSeatType = enterprise?.settings?.[SettingType.defaultSeatType]

  // Picking the org default means "follow the default", so it isn't pinned to the invite.
  const selectSeatType = (picked: ProductSeatType) => setSeatType(picked === orgDefaultSeatType ? null : picked)

  // Returning members keep their last seat in this org, so the default only applies to people new to it.
  const linkJoinerSeat = orgDefaultSeatType ? INVITE_LINK_SEAT[orgDefaultSeatType] : "your plan's default seat"

  const {
    data: teams = [],
    isLoading: isLoadingTeams,
    error: teamsError,
  } = useEnterpriseTeams(enterpriseId, { enabled: showTeamDropdown })

  const inviteValidator = useMemo(
    () =>
      makeInviteValidator(
        currentEmail,
        members.map((member) => member.email),
        invitations.map((invite) => invite.email)
      ),
    [currentEmail, members, invitations]
  )

  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation)

  const { data: inviteLinkData } = useQuery({
    // A disabled query still returns cached data, so scope the key or impersonation
    // shows the previous org's link.
    queryKey: ['teamInviteLink', enterpriseId],
    queryFn: async () => {
      const response = await getTeamInviteLink()
      return response.data
    },
    // The backend 404s View-as-Admin: the link would be personalized to the support agent.
    enabled: isAdmin && !impersonating && !!enterpriseId,
  })

  const shareableInviteUrl = inviteLinkData?.invite_url ?? null

  const validateAndSubmit = async () => {
    const collected = collectEmailsForSubmit(emails, draft)
    if ('error' in collected) {
      updateToast(collected.error, 'error')
      return
    }
    // Chips are validated at commit time, but the draft joins the batch here
    // and the member/invite lists may have changed since a chip was committed.
    for (const email of collected.emails) {
      const validationError = inviteValidator(email)
      if (validationError) {
        updateToast(`${validationError}: ${email}`, 'error')
        return
      }
    }

    setBulkCategories(null)
    const result = await onSubmit(
      // eslint-disable-next-line camelcase
      collected.emails.map((email) => ({ email, role, intended_seat_type: intendedSeatType })),
      teamId
    )
    if (result === 'error') {
      // Nothing was sent (e.g. seat cap failed the whole batch) — keep the
      // chips and draft so the user can retry without re-entering everything.
      return
    }
    if (result && result.failed.length > 0) {
      const categories = categorizeBulkResult(
        result,
        members.map((member) => member.email)
      )
      setBulkCategories(categories)
      setEmails(categories.retryable)
      setDraft('')
      return
    }
    if (result) {
      const count = result.successful.length
      updateToast(`Successfully invited ${count} user${count > 1 ? 's' : ''}`, 'success')
    }
    // Without an "automatic" option, this is how a picked seat goes back to the org default for the next invite.
    setSeatType(null)
    setEmails([])
    setDraft('')
  }

  if (isScimEnabled) {
    return (
      <div className={styles.inviteSection}>
        <InviteSectionHeading hasDepts={hasDepts} />
        <span className={styles.disclaimer}>
          User management is controlled by your identity provider via SCIM. Please add or remove users through your
          identity provider.
        </span>
      </div>
    )
  }

  return (
    <div className={styles.inviteSection}>
      <InviteSectionHeading hasDepts={hasDepts} />
      {isAdmin && !enterprise?.settings?.disableTeamTrial && (
        <span className={styles.disclaimer}>
          New users will receive a free 2 week trial when added. After their trial ends, you will be billed on your next
          renewal cycle for their seat.
        </span>
      )}
      <div className={styles.inviteForm}>
        <EmailChipInput
          emails={emails}
          onEmailsChange={setEmails}
          draft={draft}
          onDraftChange={setDraft}
          validateEmail={inviteValidator}
          placeholder="Emails to add"
          className={styles.inviteEmails}
          isDisabled={isLoading}
        />
        {enterprise?.subscription && <RoleDropdown currentRole={role} changeRole={setRole} />}
        {showSeatType && (
          <div className={styles.inviteSeatType}>
            <SeatTypeDropdown
              role={role}
              seatType={seatType ?? orgDefaultSeatType}
              ariaLabel="Seat type for new members"
              disabled={isLoading || role === 'it_admin'}
              onSelect={selectSeatType}
              placeholder={AUTOMATIC_SEAT_TYPE_LABEL}
            />
          </div>
        )}
        {showTeamDropdown && (
          <TeamDropdown
            teams={teams}
            selectedTeamId={teamId}
            onSelect={setTeamId}
            placeholder="Assign to a department"
            disabled={isLoadingTeams || !!teamsError}
            // Default 150px box wraps "Assign to a department" onto 2 lines, breaking row alignment with the other controls.
            width={210}
          />
        )}
        <Button
          onClick={validateAndSubmit}
          type="dark"
          isDisabled={(emails.length === 0 && !isValidChipEmail(normalizeEmailInput(draft))) || isLoading}
          title="Add"
          width={80}
        />
      </div>
      {bulkCategories && (
        <InviteResultsPanel rows={bulkInviteResultRows(bulkCategories)} onDismiss={() => setBulkCategories(null)} />
      )}
      {isAdmin && shareableInviteUrl && (
        <div className={styles.inviteLinkSection}>
          <span className={styles.inviteLinkLabel}>Invite Link</span>
          {showSeatType && (
            <span className={styles.disclaimer}>
              New people who join with this link are added as Members with {linkJoinerSeat}.
            </span>
          )}
          <div className={styles.inviteForm}>
            <Input value={shareableInviteUrl} type="text" className={styles.inviteEmails} readOnly />
            <Button
              onClick={async () => {
                let success = false
                try {
                  await navigator.clipboard.writeText(shareableInviteUrl)
                  success = true
                  updateToast('Link copied to clipboard', 'success')
                } catch {
                  updateToast('Failed to copy link', 'error')
                }
                try {
                  trackEvent?.(EventName.TeamInviteLinkCopied, {
                    source: 'admin_portal_team_settings',
                    platform: 'admin_portal',
                    success,
                  })
                } catch {
                  // Analytics failures must not affect the user-visible copy flow.
                }
              }}
              type="dark"
              title="Copy"
              width={80}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const TeamPage = () => {
  const currentEmail = useSelector(selectEmail)
  const isAdmin = useSelector(selectIsAdmin)
  const isScimEnabled = useSelector(selectIsScimEnabled)
  const dispatch = useDispatch<AppDispatch>()
  const isLoading = useSelector(selectIsMembersLoading)
  const hasLoaded = useSelector((state: RootState) => state.member.hasLoaded)

  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null)
  const [isRemoveMemberLoading, setIsRemoveMemberLoading] = useState(false)
  const removalUsage = useRemovalUsageLine(memberToRemove?.userId)
  const memberToRemoveName = memberToRemove
    ? [memberToRemove.firstName, memberToRemove.lastName].filter(Boolean).join(' ') || memberToRemove.email
    : ''

  const [inviteToRevoke, setInviteToRevoke] = useState<Invite | null>(null)
  const [isRevokeLoading, setIsRevokeLoading] = useState(false)

  const enterprise = useSelector(selectCurrentEnterprise)
  const hasDepts = useHasDepartments()
  const domain = enterprise?.domain
  const teamDomainStatus = useSelector(selectTeamDomainStatus)
  const domainUsers = useSelector(selectDomainUsers)
  const { members, invites } = useSortedMembers()
  const { updateToast } = useToast()
  const {
    inviteMember,
    inviteMembers,
    isLoading: isInviteLoading,
    activeDialog,
    closeDialog,
    executeAction,
  } = useTeamMemberActions()

  const sortedOrphanedUsers = useMemo(() => {
    if (!domainUsers || !members || !invites || !members.length) {
      return null
    }

    const memberSet = new Set(members.map((member) => member.email.toLowerCase()))
    const inviteSet = new Set(invites.map((invite) => invite.email.toLowerCase()))

    const orphanedUsers = domainUsers.filter(
      (user) => !memberSet.has(user.email.toLowerCase()) && !inviteSet.has(user.email.toLowerCase())
    )
    orphanedUsers.sort((a, b) => a.email.localeCompare(b.email))
    return orphanedUsers
  }, [domainUsers, members, invites])

  // Department tab is part of the V2 team-insights experience — gate it on
  // the same posthog flag that drives the Usage V2 dashboard so an enterprise
  // not on V2 doesn't see a Departments tab pointing at a backend it can't
  // exercise the rest of yet.
  const teamInsightsEnabled = !!useFeatureFlagEnabled('team-insights')
  // Separate rollout for the invite-row department picker — independent of the
  // Department tab above, so it can ship/roll back on its own schedule.
  const teamInviteDepartmentPickerEnabled = !!useFeatureFlagEnabled('team-invite-department-picker')

  // Tab descriptors carry a stable `key` so activeTab state survives changes
  // in tab composition (e.g. OtherDomainUsers tab appearing/disappearing
  // based on domain). Without this, `activeTab=2` meant "Department" on one
  // render and "OtherDomainUsers" on another, swapping the panel underneath
  // the user.
  type TabKey = 'members' | 'orphans' | 'department'
  const [activeTabKey, setActiveTabKey] = useState<TabKey>('members')
  const tabDescriptors = useMemo((): { key: TabKey; label: string }[] => {
    const teamMembersCount = members.length + invites.length
    const showOrphans = !!domain && teamDomainStatus !== TeamDomainStatus.Forbidden && !!sortedOrphanedUsers?.length
    const descriptors: { key: TabKey; label: string }[] = [
      { key: 'members', label: `${TEAM_MEMBERS_TAB} (${teamMembersCount})` },
    ]
    if (showOrphans) {
      descriptors.push({ key: 'orphans', label: `${OTHER_DOMAIN_USERS_TAB} (${sortedOrphanedUsers!.length})` })
    }
    if (isAdmin && teamInsightsEnabled) {
      descriptors.push({ key: 'department', label: DEPARTMENT_TAB })
    }
    return descriptors
  }, [domain, teamDomainStatus, sortedOrphanedUsers, members.length, invites.length, isAdmin, teamInsightsEnabled])
  const tabs = useMemo(() => tabDescriptors.map((d) => d.label), [tabDescriptors])
  const activeTabIndex = Math.max(
    0,
    tabDescriptors.findIndex((d) => d.key === activeTabKey)
  )

  // Fall back to the first tab if the previously-active one disappeared
  // (e.g. user was on OtherDomainUsers and the domain filter removed it,
  // or the team-insights flag flipped off mid-session).
  useEffect(() => {
    if (!tabDescriptors.some((d) => d.key === activeTabKey)) {
      setActiveTabKey('members')
    }
  }, [tabDescriptors, activeTabKey])

  const setActiveTab = (idx: number) => {
    const next = tabDescriptors[idx]
    if (next) setActiveTabKey(next.key)
  }
  const activeTab = activeTabIndex
  const departmentTabIndex = tabDescriptors.findIndex((d) => d.key === 'department')

  const removeMember = async () => {
    if (memberToRemove?.email === currentEmail) {
      updateToast(`You cannot remove yourself from the ${hasDepts ? 'organization' : 'team'}`, 'error')
      return
    }
    if (!memberToRemove || isRemoveMemberLoading) {
      return
    }
    try {
      setIsRemoveMemberLoading(true)
      await dispatch(removeMemberThunk({ userId: memberToRemove.userId })).unwrap()
      updateToast('Removed user', 'success')
      setMemberToRemove(null)
    } catch (error) {
      updateToast(error as string, 'error')
    } finally {
      setIsRemoveMemberLoading(false)
    }
  }

  const revokeInvite = async () => {
    if (!inviteToRevoke || isRevokeLoading) {
      return
    }
    try {
      setIsRevokeLoading(true)
      await dispatch(revokeInviteThunk({ email: inviteToRevoke.email })).unwrap()
      updateToast('Revoked invitation: Your billing plan will be updated next cycle', 'success')
      setInviteToRevoke(null)
    } catch (error) {
      updateToast(error as string, 'error')
    } finally {
      setIsRevokeLoading(false)
    }
  }

  useEffect(() => {
    if (!hasLoaded) {
      dispatch(fetchAllUsersThunk())
    }
  }, [dispatch, hasLoaded])

  return (
    <>
      {/* Associated Dialogs */}
      <TeamMemberActionDialogs activeDialog={activeDialog} onClose={closeDialog} onExecute={executeAction} />

      {/* Team Page */}
      <Helmet>
        <title>Wispr Flow | {hasDepts ? 'Organization' : 'Team'}</title>
      </Helmet>
      <div className={`${styles.container} ${styles.userContainer}`}>
        <div className={`${styles.tabTitle} ${styles.pageTabTitle}`}>
          <h6>{hasDepts ? 'Organization' : 'Team'}</h6>
          <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
        {activeTab !== departmentTabIndex && (
          <InviteSection
            onSubmit={(invites, teamId) => inviteMembers(invites, false, teamId)}
            isLoading={isInviteLoading}
            isScimEnabled={isScimEnabled}
            hasDepts={hasDepts}
            teamInsightsEnabled={teamInsightsEnabled}
            teamInviteDepartmentPickerEnabled={teamInviteDepartmentPickerEnabled}
          />
        )}

        <div className={styles.tableContainer}>
          {activeTab !== departmentTabIndex && <SeatBreakdown />}
          {isAdmin && activeTab === 0 && (
            <div className={styles.adminLine}>
              <span className={styles.disclaimer}>
                When you remove a member or revoke an invite, the seat becomes available for reuse during the current
                billing period.
                {!enterprise?.preserveContractedSeats &&
                  ' If it remains unused, it will be automatically removed at the start of the next cycle and will not be charged.'}
              </span>
              <ResendAllInvites isScimEnabled={isScimEnabled} />
            </div>
          )}

          <MemberTable
            setMemberToRemove={setMemberToRemove}
            prefetchRemoval={(member) => removalUsage.prefetch(member.userId)}
            setInviteToRevoke={setInviteToRevoke}
            isScimEnabled={isScimEnabled}
            hidden={activeTab !== 0}
          />

          {activeTab !== 0 && activeTab !== departmentTabIndex && (
            <OtherDomainUsersTable
              sortedOrphanedUsers={sortedOrphanedUsers}
              onInvite={inviteMember}
              isLoading={isInviteLoading}
              isScimEnabled={isScimEnabled}
              isDomainUsersCapped={(domainUsers?.length ?? 0) >= DOMAIN_INFO_USER_LIMIT}
            />
          )}
          {activeTab === departmentTabIndex && <Department />}
        </div>

        {isLoading && <Spinner size={30} color="black" />}

        <RemoveMemberConfirmDialog
          isOpen={!!memberToRemove}
          name={memberToRemoveName}
          usageLine={removalUsage.line}
          isUsageLoading={removalUsage.isLoading}
          scope={hasDepts ? 'organization' : 'team'}
          isRemoving={isRemoveMemberLoading}
          onConfirm={removeMember}
          onCancel={() => setMemberToRemove(null)}
        />

        <DialogWrapper
          isOpen={!!inviteToRevoke}
          title="Revoke Invite"
          subtitle={`Are you sure you want to revoke the invite for ${inviteToRevoke?.email}?`}
          close={() => setInviteToRevoke(null)}
        >
          <Button title="Revoke" onClick={revokeInvite} type="dark" width={80} isDisabled={isRevokeLoading} />
        </DialogWrapper>
      </div>
    </>
  )
}

export default TeamPage
